import checkTaskTemplateAndThemeForClasses from '@/api/classes/checkTaskTemplateAndThemeForClasses.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { NavigationContext } from '@/shared/NavigationContext/NavigationContext';
import { ThemesContext } from '@/shared/ThemesContext/ThemesContext';
import { useRouter } from 'next/router';
import { useEffect, useContext, useRef } from 'react';
import { filterItemsByTagId } from '@/modules/tags/filterByTagId';
import getMainActivity from '@/modules/activities/getMainActivity';
import { deepClone } from '@/modules/objectUtils';
import useLibraryUtils from '@/pages/Library/useLibraryUtils.hook';
import getActivity from '@/api/content/getActivity.endpoint';

export default function useThemeDetail({ activityType }) {
  const globalContext = useContext(GlobalContext);
  const themesContext = useContext(ThemesContext);
  const navigationContext = useContext(NavigationContext);
  const router = useRouter();
  const activitiesCache = useRef({});

  const { isLibrary } = useLibraryUtils();

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      loadingTheme: false,
      theme: null,
      selectedActivity: null,
      selectedAction: null,
      themeInClass: null,
    },
    actions: {
      setSelectedAction: (selectedAction) => {
        actions.setState({ selectedAction });
      },
      loadThemeEnabledForClasses: async () => {
        if (
          navigationContext.state.classes?.length ||
          globalContext.state.isTeacher
        ) {
          try {
            actions.setState({
              loadingTheme: true,
            });
            const themeInClass = await checkTaskTemplateAndThemeForClasses(
              globalContext.state.isTeacher
                ? [globalContext.state.user.teacher.internalUseClassId]
                : navigationContext.state.classes.map((clazz) => clazz.id),
              state.theme.id
            );
            actions.setState({
              themeInClass,
              loadingTheme: false,
            });
          } catch (e) {
            openAPIErrorNotification({
              title: 'There was an error',
              description:
                'There was a problem loading the tasks assigned to your classes',
              errorFeedback: e.feedbackData,
            });
          }
        } else {
          actions.setState({
            loadingTheme: false,
          });
        }
      },
      loadPage: async () => {
        let theme = null;
        let selectedActivity = null;

        const type = activityType || router.query.activityType;
        const slug = `${type}/${router.query.activitySlug}`;

        theme = themesContext.state.themes.find((theme) => {
          const activity = theme.activities.find(
            (activity) => activity.slug == slug || activity.oldSlug == slug
          );
          if (activity) {
            selectedActivity = activity;
          }
          return !!activity;
        });

        if (!theme) {
          router.replace(isLibrary ? '/library' : '/themes');
          return;
        }

        const filteredTheme = {
          ...deepClone(theme),
          activities: filterItemsByTagId(
            theme.activities,
            'activityTags',
            navigationContext.state.selectedTagId
          ),
        };

        const isNowVisible = filteredTheme.activities.some(
          (activity) => activity.id == selectedActivity.id
        );

        if (!isNowVisible) {
          selectedActivity = getMainActivity(filteredTheme);
          router.replace(getActivityUrl(selectedActivity, isLibrary));
          return;
        }

        const selectedActivityWithFullData = await getActivityWithData(
          selectedActivity,
          activitiesCache
        );

        actions.setState({
          theme: filteredTheme,
          selectedActivity: selectedActivityWithFullData,
          loading: false,
        });
      },
    },
  });

  useEffect(() => {
    if (themesContext.state.loaded && router.isReady) {
      actions.loadPage();
    }
  }, [
    themesContext.state,
    router.isReady,
    router.query.activityType,
    router.query.activitySlug,
    navigationContext.state.selectedTagId,
    activityType,
  ]);

  useEffect(() => {
    if (
      state.theme?.id &&
      globalContext.state.loaded &&
      globalContext.state.user?.teacher &&
      navigationContext.state.loaded
    ) {
      actions.loadThemeEnabledForClasses();
    }
  }, [
    state.theme?.id,
    globalContext.state.loaded,
    globalContext.state.user,
    navigationContext.state.loaded,
    navigationContext.state.classes,
  ]);

  useEffect(() => {
    if (
      state.theme?.language &&
      globalContext.state.selectedLanguage != state.theme.language
    ) {
      globalContext.actions.setSelectedLanguage(state.theme.language);
    }
  }, [state.theme?.language]);

  return { state, actions };
}

function getActivityUrl(activity, isLibrary) {
  if (!activity) {
    return isLibrary ? '/library' : '';
  }
  return `${isLibrary ? '/library/activities' : ''}/${activity.slug}`;
}

async function getActivityWithData(activity, cache) {
  if (cache.current[activity.id]) {
    return cache.current[activity.id];
  }

  const activityWithData = await getActivity(activity.id);
  cache.current[activity.id] = activityWithData;

  return activityWithData;
}
